import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import {
  type HTMLAttributes,
  type MouseEventHandler,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import OstroDynamicContent from 'app/components/OstroDynamicContent';

import useLiveChatTracking from '../hooks/useLiveChatTracking';

interface DisclaimerMessageProps extends HTMLAttributes<HTMLDivElement>, PropsWithChildren {
  message: string;
}

const DisclaimerMessage = ({ message, ...rest }: DisclaimerMessageProps) => {
  const { captureDisclaimerExpanded, captureDisclaimerPresented } = useLiveChatTracking();
  const { t } = useTranslation();
  const { data: helpCenter } = useFetchHelpCenter();

  const textRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowed, setOverflowed] = useState(false);

  const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();

      setExpanded((expanded) => !expanded);

      if (expanded) {
        captureDisclaimerExpanded();
      }
    },
    [expanded, captureDisclaimerExpanded]
  );

  useEffect(() => {
    if (!helpCenter) return;

    captureDisclaimerPresented(helpCenter?.nurses_are_online);
  }, [helpCenter, captureDisclaimerPresented]);

  useEffect(() => {
    const checkOverflow = () => {
      if (!textRef.current) return;
      setOverflowed(textRef.current.scrollHeight >= textRef.current.clientHeight);
    };

    window.addEventListener('resize', checkOverflow);
    checkOverflow();

    return () => window.removeEventListener('resize', checkOverflow);
  }, [textRef]);

  return (
    <div className="disclaimer-message bg-option p-[10px] mt-8 mb-4 rounded-[14px]">
      <OstroDynamicContent
        forwardRef={textRef}
        className="overflow-hidden text-sm [&_p]:(mb-2 empty:pb-[1em] last-of-type:mb-0)"
        style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: expanded ? 'unset' : 11,
          overflowWrap: 'anywhere',
        }}
        content={message}
        {...rest}
      />

      {overflowed && !expanded ? (
        <a
          className="block mt-[1em] text-sm text-highlight cursor-pointer"
          onClick={handleClick}
        >
          ...{t('read_more')}
        </a>
      ) : null}
    </div>
  );
};

export default DisclaimerMessage;
